import { Box, Text, Link, Drawer, useDisclosure, Portal, CloseButton } from "@chakra-ui/react"
import { useGlobalState } from "providers/globalState"
import { useConferenceFacultyProfile } from "services/emma/emma"
import { isAppView } from "src/util"
import Highlighter from "components/agenda/highlighter"
import FacultyProfile from "components/faculty/facultyProfile"
import LoadingIndicator from "components/ui/LoadingIndicator"

export default function ModeratorList({ group, inline = true, linkToProfile = true, color = "#888" }) {
    const moderatorLabelElement = inline ? "span" : "div"

    if (!group.users?.length) return null

    return (
        <Box className="program__moderators" m={0} color={color} lineHeight="1.4">
            <Text className="program__moderatorRole" s={moderatorLabelElement} fontWeight={600} pr={3}>
                {group.role.display_name}
            </Text>
            <NameList users={group.users} linkToProfile={linkToProfile} />
        </Box>
    )
}

function NameList({ users, linkToProfile }) {
    return users.map((user, i) => {
        return (
            <Text className="program__moderator" as="span" key={user.id}>
                <ConditionalLink user={user} linkToProfile={linkToProfile}>
                    <Text as="span" fontWeight={user.highlight ? 600 : null}>
                        <Highlighter hit={true} text={user.full_name} />
                    </Text>
                </ConditionalLink>
                {i < users.length - 1 && ", "}
            </Text>
        )
    })
}

function ConditionalLink({ user, linkToProfile, children }) {
    const { site } = useGlobalState()
    const { open, onOpen, onClose } = useDisclosure()
    const showProfileLink =
        !isAppView() &&
        !site.isWebView &&
        !["abstract-presenter", "challenging-case-presenter", "satellite-presenter"].includes(user.meeting_role)

    if (!linkToProfile || !site?.meeting?.enableFacultyProfileLinks || site.suppressModeratorLinks)
        return <Text as="span">{children}</Text>

    return (
        <Text as="span">
            <Link onClick={onOpen} variant="underline" _hover={{ textDecoration: "underline" }} color="#333">
                {children}
            </Link>
            <Drawer.Root
                className="facultyProfileDrawer"
                open={open}
                onOpenChange={({ open }) => !open && onClose()}
                placement="end"
                size="lg"
            >
                <Portal>
                    <Drawer.Backdrop />
                    <Drawer.Positioner>
                        <Drawer.Content>
                            {showProfileLink && (
                                <Link
                                    href={`/faculty/${user.first_name}-${user.last_name}-${user.id}`}
                                    display="block"
                                    color="#333"
                                    textDecoration="underline"
                                    m="20px 0 0 25px"
                                    target="_blank"
                                >
                                    Go to full faculty profile page
                                </Link>
                            )}
                            <Drawer.Body color="#333">
                                <RenderFacultyProfile facultyId={user.id} emmaMeetingId={site.meeting.emmaMeetingId} />
                            </Drawer.Body>
                            <Drawer.CloseTrigger asChild>
                                <CloseButton size="sm" />
                            </Drawer.CloseTrigger>
                        </Drawer.Content>
                    </Drawer.Positioner>
                </Portal>
            </Drawer.Root>
        </Text>
    )
}

function RenderFacultyProfile({ emmaMeetingId, facultyId }) {
    const { data, status } = useConferenceFacultyProfile(emmaMeetingId, facultyId)

    return (
        <>
            {status === "success" ? (
                <FacultyProfile profile={data} context="drawer" />
            ) : (
                <LoadingIndicator color="color1" />
            )}
        </>
    )
}
